import { AppIcon } from '@/components/ui/app-icon';
import { Head } from '@/components/head.component';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  iconArrowLeft,
  iconArrowUpRightFromSquare,
  iconCircleExclamation,
  iconQuestion,
} from '@/lib/icons';
import { cn } from '@/lib/utils';
import {
  createFileRoute,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import { useEffect } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { BRAND } from '@/lib/brand';

const crmFailedSearchSchema = z.object({
  error: z.string().optional(),
});

export const Route = createFileRoute('/crm/failed')({
  component: RouteComponent,
  validateSearch: crmFailedSearchSchema,
});

function RouteComponent() {
  const navigate = useNavigate();
  const { error } = useSearch({ from: '/crm/failed' });

  useEffect(() => {
    if (error) {
      toast.error('CRM Integration Failed', {
        description: getErrorMessage(error),
        duration: 10000,
      });
    }
  }, [error]);

  const getErrorMessage = (errorMsg: string) => {
    if (errorMsg.includes('permissions') || errorMsg.includes('Forbidden')) {
      return 'You do not have the required permissions to complete this integration.';
    }
    if (errorMsg.includes('access_denied')) {
      return 'Access was denied. Please ensure you have the necessary permissions.';
    }
    if (errorMsg.includes('invalid_grant')) {
      return 'The authorization code has expired. Please try connecting again.';
    }
    if (errorMsg.includes('unauthorized')) {
      return 'Authentication failed. Please check your credentials and try again.';
    }
    return 'The CRM connection could not be completed. Return to Integrations to check the connection and try again.';
  };

  const getErrorTitle = (errorMsg: string) => {
    if (errorMsg.includes('permissions') || errorMsg.includes('Forbidden')) {
      return 'Permission Error';
    }
    if (errorMsg.includes('access_denied')) {
      return 'Access Denied';
    }
    if (errorMsg.includes('invalid_grant')) {
      return 'Session Expired';
    }
    if (errorMsg.includes('unauthorized')) {
      return 'Authentication Failed';
    }
    return 'Integration Failed';
  };

  const getErrorSuggestion = (errorMsg: string) => {
    if (errorMsg.includes('permissions') || errorMsg.includes('Forbidden')) {
      return 'Please ensure you have admin access or the required permissions in your CRM account. Contact your CRM administrator if needed.';
    }
    if (errorMsg.includes('access_denied')) {
      return 'Make sure to grant all requested permissions when connecting your CRM.';
    }
    if (errorMsg.includes('invalid_grant')) {
      return 'The connection attempt took too long or was interrupted. Try connecting again.';
    }
    if (errorMsg.includes('unauthorized')) {
      return 'Verify that your account credentials are correct and try again.';
    }
    return 'Please try connecting again. If the problem persists, contact support.';
  };

  return (
    <>
      <Head heading='Integration Failed' />
      <div className='flex h-full min-h-screen flex-1 items-center justify-center bg-background p-4'>
        <Card className='w-full max-w-2xl min-w-0 border-border bg-background shadow-none'>
          <CardHeader className='space-y-4 pb-4'>
            <div className='flex items-start gap-4'>
              <div
                className={cn(
                  'rounded-full p-3',
                  'bg-red-100 dark:bg-red-900/20',
                  'shrink-0'
                )}
              >
                <AppIcon
                  icon={iconCircleExclamation}
                  className='text-red-600 dark:text-red-500'
                  style={{ fontSize: 32 }}
                />
              </div>
              <div className='min-w-0 flex-1'>
                <CardTitle className='text-lg font-semibold text-foreground'>
                  {error ? getErrorTitle(error) : 'Integration Failed'}
                </CardTitle>
                <CardDescription className='mt-2 text-sm break-words'>
                  {error
                    ? getErrorMessage(error)
                    : 'An error occurred while connecting your CRM'}
                </CardDescription>
              </div>
            </div>
          </CardHeader>

          <CardContent className='space-y-6 pt-2'>
            {error && (
              <details className='min-w-0 text-xs text-muted-foreground'>
                <summary className='cursor-pointer rounded-sm focus-visible:outline-2'>
                  Connection details
                </summary>
                <p className='mt-2 max-h-40 overflow-y-auto break-all'>
                  {error}
                </p>
              </details>
            )}
            {error && (
              <div
                className={cn(
                  'rounded-lg border p-4',
                  'border-red-200 bg-red-50',
                  'dark:border-red-900/30 dark:bg-red-900/10'
                )}
              >
                <div className='mb-2 flex items-center gap-2'>
                  <AppIcon
                    icon={iconQuestion}
                    className='text-red-700 dark:text-red-400'
                    style={{ fontSize: 16 }}
                  />
                  <h3 className='text-sm font-semibold text-red-900 dark:text-red-100'>
                    What went wrong?
                  </h3>
                </div>
                <p className='text-sm text-red-800 dark:text-red-200'>
                  {getErrorSuggestion(error)}
                </p>
              </div>
            )}

            <div className='flex flex-col gap-3 sm:flex-row'>
              <Button
                variant='destructive'
                className='flex-1 bg-red-600 hover:bg-red-700'
                onClick={() => navigate({ to: '/integrations' })}
              >
                <AppIcon
                  icon={iconArrowLeft}
                  className='mr-2'
                  style={{ fontSize: 16 }}
                />
                Back to Integrations
              </Button>
              <Button
                variant='outline'
                className='flex-1'
                onClick={() =>
                  window.open(
                    'https://docs.cleanlist.ai/features/integrations',
                    '_blank'
                  )
                }
              >
                <AppIcon
                  icon={iconArrowUpRightFromSquare}
                  className='mr-2'
                  style={{ fontSize: 16 }}
                />
                View documentation
              </Button>
            </div>

            <div
              className={cn(
                'rounded-lg border p-4 text-center',
                'border-gray-200 bg-gray-50',
                'dark:border-gray-700 dark:bg-gray-800/50'
              )}
            >
              <p className='text-sm text-muted-foreground'>
                Require help? Contact the {BRAND.dotName} support team at{' '}
                <a
                  href='mailto:support@cleanlist.ai'
                  className='font-medium text-primary hover:underline'
                >
                  support@cleanlist.ai
                </a>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
